import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { api as http } from '../lib/api.ts';
import { BlockList } from './blocks.tsx';
import { DatabaseView } from './database.tsx';
import {
  EditorCtx, childrenOf, makeEditorApi, useEnsureTrailingBlock, useTrailingClick,
} from './editor.tsx';
import { item, separator, useMenu } from './menu.tsx';
import { useBlockDrag } from './drag.ts';
import { useBlockSelection } from './select.ts';
import { SlashMenu, type SlashState } from './slash.tsx';
import {
  archivePage, createPage, duplicatePage, openPage, setCaret, updatePage, useStore,
} from './store.ts';
import type { Block } from './types.ts';

const ICONS = [
  '📄', '📋', '📝', '📒', '📕', '📗', '📘', '📙', '🗂️', '📁', '🗒️', '📌',
  '⭐', '🔥', '💡', '🎯', '✅', '🚀', '🧠', '🛠️', '📊', '📈', '🗓️', '⏰',
  '🌱', '🍀', '🌊', '🌙', '☀️', '🏠', '🧭', '🔑', '💬', '❤️', '🎨', '🎵',
];

export function PageView() {
  const doc = useStore((s) => s.doc);
  const loading = useStore((s) => s.loadingDoc);
  const [slash, setSlash] = useState<SlashState>(null);
  const [menu, openMenu] = useMenu();
  const [showOriginal, setShowOriginal] = useState(false);

  const blocks: Block[] = doc?.blocks ?? [];
  const api = useMemo(
    () => makeEditorApi(blocks, (block, rect) => setSlash({ blockId: block.id, rect })),
    [blocks],
  );

  useEnsureTrailingBlock(api, !!doc && !doc.page.is_database);
  useBlockSelection(api);
  useBlockDrag(api);
  const onTrailingClick = useTrailingClick(api);

  if (!doc) {
    return (
      <main className="page">
        <div className="page-blank">
          {loading ? 'Opening…' : 'Pick a page on the left, or make a new one.'}
        </div>
      </main>
    );
  }

  const { page } = doc;

  return (
    <EditorCtx.Provider value={api}>
      <main className={`page${page.full_width ? ' wide' : ''}`}>
        <header className="page-top">
          <nav className="crumbs" aria-label="Breadcrumb">
            {doc.breadcrumb.map((crumb, i) => (
              <span key={crumb.id}>
                {i > 0 ? <span className="crumb-sep">/</span> : null}
                <button type="button" className="crumb" onClick={() => void openPage(crumb.id)}>
                  <span aria-hidden>{crumb.icon || '📄'}</span> {crumb.title || 'Untitled'}
                </button>
              </span>
            ))}
          </nav>

          <div className="page-tools">
            <SaveDot />
            <button
              type="button" className="page-fav" aria-pressed={page.favorite}
              aria-label={page.favorite ? 'Remove from favorites' : 'Add to favorites'}
              onClick={() => void updatePage(page.id, { favorite: !page.favorite })}
            >{page.favorite ? '★' : '☆'}</button>
            <button
              type="button" className="page-more" aria-label="Page options"
              onClick={(e) => openMenu(e, [
                item(page.full_width ? 'Narrow width' : 'Full width',
                  () => void updatePage(page.id, { full_width: !page.full_width })),
                item('Duplicate', () => void duplicatePage(page.id)),
                item('Copy as markdown', () => {
                  void http.get<{ markdown: string }>(`/api/pages/${page.id}/markdown`)
                    .then((res) => navigator.clipboard?.writeText(res.markdown));
                }),
                ...(doc.legacy_markdown
                  ? [separator(), item('View the original text', () => setShowOriginal(true),
                      { hint: 'before the rewrite' })]
                  : []),
                separator(),
                item('Move to trash', () => void archivePage(page.id), { danger: true }),
              ])}
            >⋯</button>
          </div>
        </header>

        <div className="page-body">
          <div className="page-head">
            <button
              type="button" className="page-icon" aria-label="Change the page icon"
              onClick={(e) => openMenu(e, [
                ...(page.icon ? [item('Remove icon', () => void updatePage(page.id, { icon: '' }))] : []),
                ...ICONS.map((icon) => item(icon, () => void updatePage(page.id, { icon }))),
              ])}
            >{page.icon || '📄'}</button>

            <Title
              key={page.id}
              value={page.title}
              onChange={(title) => void updatePage(page.id, { title })}
              onEnter={() => {
                const first = childrenOf(blocks, null)[0];
                if (first) setCaret({ blockId: first.id, at: 'start' });
                else api.insertAfter(null, 'text');
              }}
            />
          </div>

          {page.is_database ? (
            <DatabaseView dbId={page.id} />
          ) : (
            <div className="page-blocks" onClick={onTrailingClick}>
              <BlockList parentId={null} />
              <div className="page-trailing" onClick={onTrailingClick} />
            </div>
          )}

          {doc.children.length && !page.is_database ? (
            <section className="page-children">
              <h4>Pages inside</h4>
              {doc.children.map((child) => (
                <button key={child.id} type="button" className="page-child" onClick={() => void openPage(child.id)}>
                  <span aria-hidden>{child.icon || (child.is_database ? '🗂️' : '📄')}</span>
                  {child.title || 'Untitled'}
                </button>
              ))}
            </section>
          ) : null}

          <button type="button" className="page-subpage" onClick={() => void createPage({ parentId: page.id })}>
            + Add a page inside
          </button>
        </div>

        <SlashMenu state={slash} api={api} onClose={() => setSlash(null)} />
        {menu}
        {showOriginal && doc.legacy_markdown ? (
          <Original text={doc.legacy_markdown} onClose={() => setShowOriginal(false)} />
        ) : null}
      </main>
    </EditorCtx.Provider>
  );
}

function Title({
  value, onChange, onEnter,
}: {
  value: string;
  onChange: (next: string) => void;
  onEnter: () => void;
}) {
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const [text, setText] = useState(value);

  useEffect(() => { setText(value); }, [value]);

  // Grow with the text rather than scrolling inside a fixed box.
  //
  // Measuring only when the text changes is not enough: the first measurement
  // can land before the layout has settled, and a 26-character title then sits
  // in a box nineteen lines tall. Watch the container instead, and act only on
  // a width change — reacting to its height would be a loop, since the height
  // is what this sets.
  useLayoutEffect(() => {
    const el = ref.current;
    const box = el?.parentElement;
    if (!el || !box) return;

    let lastWidth = -1;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      if (width === lastWidth) return;
      lastWidth = width;
      size(el);
    });
    observer.observe(box);
    size(el);
    return () => observer.disconnect();
  }, []);

  useLayoutEffect(() => { if (ref.current) size(ref.current); }, [text]);

  return (
    <textarea
      ref={ref}
      className="page-title"
      value={text}
      rows={1}
      placeholder="Untitled"
      aria-label="Page title"
      onChange={(e) => { setText(e.target.value); onChange(e.target.value); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); onEnter(); }
        if (e.key === 'ArrowDown' && e.currentTarget.selectionStart === text.length) {
          e.preventDefault();
          onEnter();
        }
      }}
    />
  );
}

function size(el: HTMLTextAreaElement): void {
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}

function SaveDot() {
  const saving = useStore((s) => s.saving);
  if (saving === 'idle') return null;
  return <span className={`savedot ${saving}`}>{saving === 'saving' ? 'Saving…' : 'Saved'}</span>;
}

/** The exact text a page was built from, kept so an import can be checked. */
function Original({ text, onClose }: { text: string; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="sheet" role="dialog" aria-label="Original text" onClick={onClose}>
      <div className="sheet-box" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <strong>Original text</strong>
          <span>Exactly what this page was built from. Nothing here was changed.</span>
          <button type="button" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <pre className="sheet-body">{text}</pre>
      </div>
    </div>
  );
}
